<template>
  <div id="sections">
    <v-toolbar color="page-toolbar">
      <v-toolbar-title class="text-body-large"> Liste des rivières </v-toolbar-title>
    </v-toolbar>
    <div>
      <v-fab-transition>
        <v-btn class="filter-fab" color="page-toolbar-accent" icon size="small" @click="filterDrawer = !filterDrawer">
          <v-icon v-if="!emptyFilters">mdi-filter-menu</v-icon>
          <v-icon v-if="emptyFilters">mdi-filter-menu-outline</v-icon>
        </v-btn>
      </v-fab-transition>
      <v-navigation-drawer v-model="filterDrawer" temporary>
        <v-list-item>
          <v-list-item-title class="text-title-large"> Filtres </v-list-item-title>
        </v-list-item>
        <v-divider class="mt-2"></v-divider>
        <v-text-field v-model="search" label="Recherche par nom" clearable></v-text-field>

        <v-list density="compact" nav>
          <v-list-item :class="{ 'text-primary': searchCriterias.isLiked }" title="Uniquement les favoris">
            <template v-slot:append>
              <v-checkbox-btn v-model="searchCriterias.isLiked" color="primary"></v-checkbox-btn>
            </template>
          </v-list-item>

          <v-list-item :class="{ 'text-primary': searchCriterias.freeAccessOnly }" title="Uniquement accès gratuit">
            <template v-slot:append>
              <v-checkbox-btn v-model="searchCriterias.freeAccessOnly" color="primary"></v-checkbox-btn>
            </template>
          </v-list-item>

          <v-list-item :class="{ 'text-error': searchCriterias.dangersOnly }" title="Uniquement avec dangers actifs">
            <template v-slot:append>
              <v-checkbox-btn v-model="searchCriterias.dangersOnly" color="danger"></v-checkbox-btn>
            </template>
          </v-list-item>

          <v-divider class="mt-2"></v-divider>

          <v-list-group value="regions">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props">
                <v-list-item-title>
                  <strong>Régions</strong>
                  <a
                    class="text-right"
                    style="float: right"
                    v-if="searchCriterias.regions.length > 0"
                    @click.stop="clearRegionFilters()"
                  >
                    <v-icon size="small">mdi-filter</v-icon> (Effacer tout)
                  </a>
                </v-list-item-title>
              </v-list-item>
            </template>
            <template v-for="(item, i) in regions" :key="item ? item.name : `divider-${i}`">
              <v-divider v-if="!item"></v-divider>
              <v-list-item
                v-else
                :title="item.name"
                :class="{ 'text-primary': searchCriterias.regions.includes(item.name) }"
              >
                <template v-slot:append>
                  <v-checkbox-btn v-model="searchCriterias.regions" :value="item.name" color="primary"></v-checkbox-btn>
                </template>
              </v-list-item>
            </template>
          </v-list-group>
          <v-list-group value="classes">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props">
                <v-list-item-title>
                  <strong>Classes</strong>
                  <a
                    class="text-right"
                    style="float: right"
                    v-if="searchCriterias.classes.length > 0"
                    @click.stop="clearClassificationFilters()"
                  >
                    <v-icon size="small">mdi-filter</v-icon> (Effacer tout)
                  </a>
                </v-list-item-title>
              </v-list-item>
            </template>
            <template v-for="(item, i) in classes" :key="item ? item.class_level : `divider-${i}`">
              <v-divider v-if="!item"></v-divider>
              <v-list-item
                v-else
                :title="item.class_level"
                :class="{ 'text-primary': searchCriterias.classes.includes(item.class_level) }"
              >
                <template v-slot:append>
                  <v-checkbox-btn
                    v-model="searchCriterias.classes"
                    :value="item.class_level"
                    color="primary"
                  ></v-checkbox-btn>
                </template>
              </v-list-item>
            </template>
          </v-list-group>
          <v-list-group value="levels">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props">
                <v-list-item-title>
                  <strong>Niveaux</strong>
                  <a
                    class="text-right"
                    style="float: right"
                    v-if="searchCriterias.levels.length > 0"
                    @click.stop="clearLevelFilters()"
                  >
                    <v-icon size="small">mdi-filter</v-icon> (Effacer tout)
                  </a>
                </v-list-item-title>
              </v-list-item>
            </template>
            <template v-for="(item, i) in levels" :key="item ? `item-${i}.text` : `divider-${i}`">
              <v-divider v-if="!item"></v-divider>
              <v-list-item
                v-else
                :title="item.text"
                :class="{ [`text-${item.color}-accent-4`]: searchCriterias.levels.includes(item.value) }"
              >
                <template v-slot:append>
                  <v-checkbox-btn
                    v-model="searchCriterias.levels"
                    :value="item.value"
                    :color="item.color ? `${item.color}-accent-4` : 'primary'"
                  ></v-checkbox-btn>
                </template>
              </v-list-item>
            </template>
          </v-list-group>
        </v-list>
      </v-navigation-drawer>
      <v-skeleton-loader
        v-if="loadingSection || loadingFilters"
        type="table-heading, list-item-three-line@8"
        :loading-text="loadingFilters ? 'Application de vos filtres...' : 'Chargement des sections...'"
      ></v-skeleton-loader>
      <sectionDataTable
        v-else
        :sections="filteredSections"
        :isMember="isMember"
        :authUser="authUser"
        :default-regions-expanded="sectionDataTableRegionsExpandedByDefault"
        @liked-changed="handleLikedChanged"
      ></sectionDataTable>
    </div>
  </div>
</template>

<script>
  import SectionDataTable from "./components/SectionDataTable.vue"; // Correctly import SectionDataTable
  import { RiversService } from "@/services/Rivers.service"; // Import RiversService
  import { auth } from "@/firebase"; // Import Firebase auth
  import { UserService } from "@/store/modules/user"; // Import UserService
  import { signalPrerenderReady } from "@/utils/seo";

  const defaultSearchCriterias = {
    regions: [],
    classes: [],
    levels: [],
    isLiked: false,
    freeAccessOnly: false,
    dangersOnly: false,
  };
  const riverListViewCache = {
    initialized: false,
    search: "",
    debouncedSearch: "",
    searchCriterias: { ...defaultSearchCriterias },
    searchCriteriasDb: { ...defaultSearchCriterias },
    likedSections: [],
  };
  let sectionsBindingRequested = false;
  let regionsBindingRequested = false;

  export default {
    components: { SectionDataTable }, // Register SectionDataTable in the components section
    name: "sections",
    props: {
      isMember: Boolean,
    },
    data() {
      return {
        filterDrawer: false,
        search: riverListViewCache.search,
        debouncedSearch: riverListViewCache.debouncedSearch,
        searchDebounceTimer: null,
        loadingSection: true,
        loadingFilters: true,
        searchCriterias: JSON.parse(JSON.stringify(riverListViewCache.searchCriterias)),
        searchCriteriasDb: JSON.parse(JSON.stringify(riverListViewCache.searchCriteriasDb)),
        classes: RiversService.getClassLevels(),
        likedSections: [...riverListViewCache.likedSections], // Store liked sections here
        sectionDataTableRegionsExpandedByDefault: true,
        prerenderReadySignaled: false,
      };
    },
    computed: {
      authUser() {
        return auth.currentUser; // Access the current user from Firebase auth
      },
      regions() {
        return this.$store.getters.regions || []; // Ensure regions is always an array
      },
      sections() {
        const likedSectionIds = new Set(this.likedSections.map((doc) => doc.id));
        const allSections = this.$store.getters["SectionsService/sections"] || []; // Ensure sections is always an array
        return allSections.map((section) => {
          const sectionId = section.id || section.sectionId || section.alias;
          const sectionName = (section.name || "").toLowerCase();
          const riverName = (section.river || "").toLowerCase();

          return {
            ...section,
            id: sectionId, // Normalize id for downstream filters/components
            isLiked: Boolean(sectionId) && likedSectionIds.has(sectionId), // Match liked docs against normalized id
            _searchText: `${sectionName}|${riverName}`,
          };
        });
      },
      levels() {
        return this.$store.getters.levels || []; // Ensure levels is always an array
      },
      filteredSections() {
        const allSections = this.sections;
        const searchTerm = (this.debouncedSearch || "").trim().toLowerCase();
        const hasLikedFilter = this.searchCriterias.isLiked;
        const hasFreeAccessFilter = this.searchCriterias.freeAccessOnly;
        const hasDangersFilter = this.searchCriterias.dangersOnly;
        const hasRegionFilter = this.searchCriterias.regions.length > 0;
        const hasClassFilter = this.searchCriterias.classes.length > 0;
        const hasLevelFilter = this.searchCriterias.levels.length > 0;
        const hasSearchFilter = searchTerm.length > 0;

        if (
          !hasLikedFilter &&
          !hasFreeAccessFilter &&
          !hasDangersFilter &&
          !hasRegionFilter &&
          !hasClassFilter &&
          !hasLevelFilter &&
          !hasSearchFilter
        ) {
          return allSections;
        }

        const regionSet = hasRegionFilter ? new Set(this.searchCriterias.regions) : null;
        const classSet = hasClassFilter ? new Set(this.searchCriterias.classes) : null;
        const levelSet = hasLevelFilter ? new Set(this.searchCriterias.levels) : null;

        return allSections.filter((section) => {
          // Non-members can only see free access sections when filtering by level
          if (!this.isMember && hasLevelFilter && section.free_access !== true) {
            return false;
          }
          if (hasLikedFilter && !section.isLiked) {
            return false;
          }
          if (hasFreeAccessFilter && section.free_access !== true) {
            return false;
          }
          // active_dangers_count is kept by the onDangerWritten Cloud Function
          if (hasDangersFilter && !(section.active_dangers_count > 0)) {
            return false;
          }
          if (hasRegionFilter && !regionSet.has(section.region)) {
            return false;
          }
          if (hasClassFilter && !classSet.has(section.class_level)) {
            return false;
          }
          if (hasLevelFilter && !levelSet.has(section.level_status)) {
            return false;
          }
          if (hasSearchFilter && !section._searchText.includes(searchTerm)) {
            return false;
          }
          return true;
        });
      },
      emptyFilters() {
        if (this.searchCriterias.regions.length > 0) {
          return false;
        }
        if (this.searchCriterias.levels.length > 0) {
          return false;
        }
        if (this.searchCriterias.classes.length > 0) {
          return false;
        }
        if (this.searchCriterias.isLiked) {
          return false;
        }
        if (this.searchCriterias.freeAccessOnly) {
          return false;
        }
        if (this.searchCriterias.dangersOnly) {
          return false;
        }
        if (this.search) {
          if (this.search.length > 0) {
            return false;
          }
        }
        return true;
      },
    },
    async created() {
      try {
        const hasSectionsInStore = (this.$store.getters["SectionsService/sections"] || []).length > 0;
        const hasRegionsInStore = (this.$store.getters.regions || []).length > 0;
        const hasLevelsInStore = (this.$store.getters.levels || []).length > 0;
        const canUseWarmCache =
          riverListViewCache.initialized && hasSectionsInStore && hasRegionsInStore && hasLevelsInStore;

        if (canUseWarmCache) {
          this.loadingSection = false;
          this.loadingFilters = false;
          // Keep cache fresh without blocking first paint when returning from detail.
          this.refreshUserPreferencesAndLikes(false).catch((error) => {
            console.error("Error refreshing cached user filters:", error);
          });
          return;
        }

        this.loadingSection = true;
        this.loadingFilters = true;

        // Start loading section data immediately while user context is resolved.
        const dataLoadPromise = Promise.all([this.getSections(), this.getRegions(), this.getLevels()]);

        await this.refreshUserPreferencesAndLikes(true);

        this.loadingFilters = false;
        await dataLoadPromise;
        riverListViewCache.initialized = true;
        this.persistViewCache();
      } catch (error) {
        console.error("Error during data loading:", error);
      } finally {
        this.loadingFilters = false;
        this.loadingSection = false;
        this.maybeSignalPrerenderReady();
      }
    },
    methods: {
      maybeSignalPrerenderReady() {
        if (this.prerenderReadySignaled || this.loadingSection || this.loadingFilters) {
          return;
        }

        this.$nextTick(() => {
          signalPrerenderReady();
          this.prerenderReadySignaled = true;
        });
      },
      persistViewCache() {
        riverListViewCache.search = this.search || "";
        riverListViewCache.debouncedSearch = this.debouncedSearch || "";
        riverListViewCache.searchCriterias = JSON.parse(JSON.stringify(this.searchCriterias || defaultSearchCriterias));
        riverListViewCache.searchCriteriasDb = JSON.parse(
          JSON.stringify(this.searchCriteriasDb || defaultSearchCriterias),
        );
        riverListViewCache.likedSections = [...(this.likedSections || [])];
      },
      async refreshUserPreferencesAndLikes(useLoadingState = true) {
        if (useLoadingState) {
          this.loadingFilters = true;
        }

        try {
          const user = await this.getAuthUserOnce();
          if (user && user.uid) {
            const [userFilters, likedSectionsSnapshot, userUiPreferences] = await Promise.all([
              UserService.actions.getUserFiltersByUid(user.uid),
              UserService.actions.getLikedSections(user.uid),
              UserService.actions.getUserUiPreferencesByUid(user.uid),
            ]);

            this.searchCriterias = {
              regions: userFilters?.regions || [],
              classes: userFilters?.classes || [],
              levels: userFilters?.levels || [],
              isLiked: Boolean(userFilters?.isLiked),
              freeAccessOnly: Boolean(userFilters?.freeAccessOnly),
              dangersOnly: Boolean(userFilters?.dangersOnly),
            };
            this.searchCriteriasDb = JSON.parse(JSON.stringify(this.searchCriterias));

            this.likedSections = likedSectionsSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            this.sectionDataTableRegionsExpandedByDefault = Boolean(
              userUiPreferences?.sectionDataTableRegionsExpandedByDefault,
            );
          } else {
            this.searchCriterias = { ...defaultSearchCriterias };
            this.searchCriteriasDb = JSON.parse(JSON.stringify(this.searchCriterias));
            this.likedSections = [];
            this.sectionDataTableRegionsExpandedByDefault = true;
          }
        } finally {
          if (useLoadingState) {
            this.loadingFilters = false;
          }
        }
      },
      getAuthUserOnce() {
        if (auth.currentUser !== null) {
          return Promise.resolve(auth.currentUser);
        }

        return new Promise((resolve) => {
          const unsubscribe = auth.onAuthStateChanged((user) => {
            unsubscribe();
            resolve(user);
          });
        });
      },
      async getSections() {
        try {
          if (sectionsBindingRequested) {
            return;
          }
          await this.$store.dispatch("SectionsService/bindSections");
          sectionsBindingRequested = true;
        } catch (error) {
          console.error("Error fetching sections:", error);
        }
      },
      async getLevels() {
        try {
          if ((this.$store.getters.levels || []).length > 0) {
            return;
          }
          await this.$store.dispatch("fetchLevels");
        } catch (error) {
          console.error("Error fetching levels:", error);
        }
      },
      async getRegions() {
        try {
          if (regionsBindingRequested) {
            return;
          }
          await this.$store.dispatch("bindRegions");
          regionsBindingRequested = true;
        } catch (error) {
          console.error("Error fetching regions:", error);
        }
      },
      async saveFiltersPreferences() {
        const currentUser = auth.currentUser;
        if (!currentUser || !currentUser.uid) {
          // Guests can still use local filters; only persist to DB for authenticated users.
          this.searchCriteriasDb = JSON.parse(JSON.stringify(this.searchCriterias));
          return;
        }

        await UserService.actions.setUserFilters(currentUser.uid, this.searchCriterias);
        this.searchCriteriasDb = JSON.parse(JSON.stringify(this.searchCriterias));
      },
      clearRegionFilters() {
        this.searchCriterias.regions = [];
      },
      clearClassificationFilters() {
        this.searchCriterias.classes = [];
      },
      clearLevelFilters() {
        this.searchCriterias.levels = [];
      },
      handleLikedChanged({ sectionId, isLiked }) {
        if (!sectionId) {
          return;
        }

        const index = this.likedSections.findIndex((section) => section.id === sectionId);

        if (isLiked && index === -1) {
          this.likedSections = [...this.likedSections, { id: sectionId }];
          return;
        }

        if (!isLiked && index !== -1) {
          this.likedSections = this.likedSections.filter((section) => section.id !== sectionId);
        }

        this.persistViewCache();
      },
    },
    watch: {
      loadingSection() {
        this.maybeSignalPrerenderReady();
      },
      loadingFilters() {
        this.maybeSignalPrerenderReady();
      },
      search: {
        immediate: true,
        handler(newSearch) {
          if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
          }

          this.searchDebounceTimer = setTimeout(() => {
            this.debouncedSearch = newSearch || "";
            this.persistViewCache();
          }, 180);

          this.persistViewCache();
        },
      },
      searchCriterias: {
        deep: true,
        handler() {
          this.persistViewCache();
          if (JSON.stringify(this.searchCriterias) !== JSON.stringify(this.searchCriteriasDb)) {
            this.saveFiltersPreferences();
          }
        },
        immediate: false,
      },
    },
    beforeUnmount() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.persistViewCache();
    },
  };
</script>

<style lang="scss" scoped>
  // Vuetify 2 "fab fixed right": stays on the right edge, just below the toolbar
  .filter-fab {
    position: fixed;
    right: 16px;
    margin-top: -36px;
    z-index: 5;
  }
</style>
